import { AttributeOffsets, deltaX as toDeltaX } from "./attributeOffsets";
import { agingOffset, effectiveAgeDays } from "./beanAging";
import type { PredictorConfig } from "./config";
import { daysBetween, type IsoDate } from "./dates";
import { clampToDial, midpoint, type Bean, type Grinder } from "./model";
import type { PseudoObservation } from "./predictor";

/**
 * Where a new bean's first shot should start. Mirrors StartingPoint.java.
 *
 *   x_dialed0(bean) = x_dialed + f(age at last shot) / |b0|        ← normalised to a fresh (age-0) bean
 *   x_ref(grinder)  = weighted mean over dialed-in past beans of (x_dialed0 − Δx_bean), weight 0.5^(days/60)
 *                     → fallback grinders.referenceSetting → fallback dial midpoint
 *   x_prior(bean)   = clamp(x_ref + Δx_bean − f(bean's effective age today) / |b0|)
 */
export type RefSource = "HISTORY" | "GRINDER_SETTING" | "MIDPOINT";

export interface BeanSummary {
  readonly beanId: number;
  /** the bean's own predicted setting from its shots alone, as of its last shot */
  readonly xDialed: number;
  /** Δx for that bean on this grinder (−δ_bean / b0) */
  readonly deltaX: number;
  readonly lastShotDate: IsoDate;
  /** f(effective age at last shot) for that bean (decaf-aware), in ln-time */
  readonly agingLnAtLastShot: number;
}

export interface Reference {
  /** age-0 reference setting for a reference bean (medium, washed, standard, caffeinated) */
  readonly xRef: number;
  readonly source: RefSource;
  readonly contributingBeans: number;
}

export interface Prior {
  readonly xPrior: number;
  readonly deltaBean: number;
  readonly deltaX: number;
  readonly agingShiftX: number;
  readonly reference: Reference;
  readonly sourceLabel: string;
}

/** Convenience for callers building a summary from a fitted bean. */
export function beanSummary(bean: Bean, g: Grinder, xDialed: number, lastShotDate: IsoDate,
                            offsets: AttributeOffsets, cfg: PredictorConfig): BeanSummary {
  return {
    beanId: bean.id ?? -1,
    xDialed,
    deltaX: toDeltaX(offsets.deltaBean(bean, cfg), g.priorSlopeB),
    lastShotDate,
    agingLnAtLastShot: agingOffset(effectiveAgeDays(bean, lastShotDate), cfg, bean.decaf),
  };
}

export function reference(g: Grinder, pastBeans: readonly BeanSummary[], today: IsoDate, cfg: PredictorConfig): Reference {
  const absB = Math.abs(g.priorSlopeB);
  let sumW = 0, sum = 0, n = 0;
  for (const b of pastBeans) {
    const days = Math.max(0, daysBetween(b.lastShotDate, today));
    const w = Math.pow(0.5, days / cfg.referenceHalfLifeD);
    const xDialed0 = b.xDialed + b.agingLnAtLastShot / absB;
    sumW += w;
    sum += w * (xDialed0 - b.deltaX);
    n++;
  }
  if (n > 0 && sumW > 0) return { xRef: clampToDial(g, sum / sumW), source: "HISTORY", contributingBeans: n };
  if (g.referenceSetting !== null) return { xRef: g.referenceSetting, source: "GRINDER_SETTING", contributingBeans: 0 };
  return { xRef: midpoint(g), source: "MIDPOINT", contributingBeans: 0 };
}

/** The prior for `bean` as of `today` (its effective age today sets the aging shift). */
export function prior(g: Grinder, bean: Bean, ref: Reference, offsets: AttributeOffsets, today: IsoDate, cfg: PredictorConfig): Prior {
  const delta = offsets.deltaBean(bean, cfg);
  const dx = toDeltaX(delta, g.priorSlopeB);
  const agingShiftX = -agingOffset(effectiveAgeDays(bean, today), cfg, bean.decaf) / Math.abs(g.priorSlopeB);
  const xPrior = clampToDial(g, ref.xRef + dx + agingShiftX);
  return { xPrior, deltaBean: delta, deltaX: dx, agingShiftX, reference: ref, sourceLabel: sourceLabel(ref, delta) };
}

export function asPseudoObservation(p: Prior, cfg: PredictorConfig): PseudoObservation {
  return { x: p.xPrior, lnT: Math.log(cfg.targetTimeS), weight: cfg.priorInterceptWeight };
}

/** Wording for the card: where the starting point came from. */
export function sourceLabel(ref: Reference, deltaBean: number): string {
  const hasOffset = Math.abs(deltaBean) > 1e-12;
  switch (ref.source) {
    case "HISTORY": return `from your history (${ref.contributingBeans}${ref.contributingBeans === 1 ? " bean)" : " beans)"}`;
    case "GRINDER_SETTING": return hasOffset ? "from bean type" : "from grinder reference";
    case "MIDPOINT": return hasOffset ? "from bean type" : "";
  }
}
